import { Outlet } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { CatalogMaterialsDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import i18n from 'modules/core/lib/i18n'

import {
  MaterialItem,
  MaterialsList,
  getSortableTitle,
} from 'modules/trainings/components/MaterialsList'

export default function CatalogMaterialsPage() {
  const { navigation } = useNavigation()
  const { trainingId } = useStrictParams<'trainingId'>()

  const source = useGraphqlDataCollectionSource({
    document: CatalogMaterialsDocument,
    search: {
      enabled: true,
    },
    mapData: (data): MaterialItem[] => {
      const trainingFiles = data.trainings.trainingFilesConnection.nodes.map((file) => ({
        id: file.id,
        filename: file.filename,
        title: file.title,
        contentType: file.contentType,
        type: 'training' as const,
        size: file.size,
      }))

      const trainingLinks = data.trainings.trainingLinksConnection.nodes.map((link) => ({
        id: link.id,
        title: link.title,
        url: link.url,
        type: 'link' as const,
      }))

      const trainingClassFiles = data.trainings.trainingClassesConnection.nodes.flatMap(
        (trainingClass) =>
          trainingClass.classFilesConnection.nodes.map((file) => ({
            id: file.id,
            title: file.title,
            filename: file.filename,
            contentType: file.contentType,
            type: 'trainingClass' as const,
            size: file.size,
          }))
      )

      const trainingClassLinks = data.trainings.trainingClassesConnection.nodes.flatMap(
        (trainingClass) =>
          trainingClass.classLinksConnection.nodes.map((link) => ({
            id: link.id,
            title: link.title,
            url: link.url,
            type: 'link' as const,
          }))
      )

      return [
        ...trainingFiles,
        ...trainingLinks,
        ...trainingClassFiles,
        ...trainingClassLinks,
      ].sort((a, b) => (getSortableTitle(a) > getSortableTitle(b) ? 1 : -1))
    },
    itemUrl: (item) => {
      switch (item.type) {
        case 'link': {
          return item.url
        }
        default: {
          return navigation.trainings.catalog.detail.materials.previewFile.fullPath({
            trainingId,
            fileId: item.id,
            fileType: item.type,
          })
        }
      }
    },
    mapVariables: ({ search }) => ({
      trainingId: Number(trainingId),
      search: search,
    }),
    mapPagination: (data) => {
      const trainingFiles = data.trainings.trainingFilesConnection.totalCount ?? 0
      const trainingLinks = data.trainings.trainingLinksConnection.totalCount ?? 0
      const trainingClassFiles = data.trainings.trainingClassesConnection.nodes.reduce(
        (total, trainingClass) => total + (trainingClass.classFilesConnection.totalCount ?? 0),
        0
      )
      const trainingClassLinks = data.trainings.trainingClassesConnection.nodes.reduce(
        (total, trainingClass) => total + (trainingClass.classLinksConnection.totalCount ?? 0),
        0
      )
      return {
        total: trainingFiles + trainingLinks + trainingClassFiles + trainingClassLinks,
      }
    },
  })

  return (
    <Box gap="s16">
      <SectionHeader
        title={i18n.t('trainings.training_catalog.materials.section_header.title')}
        description={i18n.t('trainings.training_catalog.materials.section_header.description')}
        separator="bottom"
      />
      <MaterialsList
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t(
              'trainings.training_catalog.materials.datacollection.empty_state.no_data.title'
            ),
            description: i18n.t(
              'trainings.training_catalog.materials.datacollection.empty_state.no_data.description'
            ),
          },
        }}
      />
      <Outlet />
    </Box>
  )
}
